import type { APIContext } from "astro";

import { getUserDetails } from "@/features/user/services/api";
import { universalStorage } from "@/utils/universal-storage";

import { authByToken } from "./services/api";
import { logger } from "@/services/logger";
import { getAppConfig } from "@/services/appConfig";

export const ACCESS_TOKEN_COOKIE = "access_token";
export const REFRESH_TOKEN_COOKIE = "refresh_token";

export async function handleRequestAuth(Astro: APIContext) {
  const { access_token, refresh_token } = await authenticateUsingTokenOrCookie(Astro);
  if (!access_token && !refresh_token) return;

  await Promise.all([setUserDetails(), setAppConfig()]);
}

async function authenticateUsingTokenOrCookie(Astro: APIContext) {
  let access_token = Astro.cookies.get(ACCESS_TOKEN_COOKIE)?.value;
  let refresh_token = Astro.cookies.get(REFRESH_TOKEN_COOKIE)?.value;

  try {
    const token = Astro.url.searchParams.get("token");
    if (token) {
      const auth = await authByToken(token);
      access_token = auth.access_token;
      refresh_token = auth.refresh_token;

      const cookieConfig = getAuthCookieConfig(Astro);
      Astro.cookies.set(ACCESS_TOKEN_COOKIE, access_token, cookieConfig);
      Astro.cookies.set(REFRESH_TOKEN_COOKIE, refresh_token, {
        ...cookieConfig,
        expires: new Date(new Date().setDate(new Date().getDate() + 300)), // today + 300 days
      });
    }
  } catch (e) {
    logger.error("authByToken error", e);
  }

  Astro.locals.auth = { access_token, refresh_token };

  return { access_token, refresh_token };
}

async function setUserDetails() {
  try {
    const userDetails = await getUserDetails();
    if (userDetails) {
      universalStorage.set("user_id", userDetails.user_id ? String(userDetails.user_id) : undefined);
      universalStorage.set("first_name", userDetails.first_name);
      universalStorage.set("last_name", userDetails.last_name);
      universalStorage.set("email", userDetails.email);
      universalStorage.set("subscription_type", userDetails.subscription_type);
      universalStorage.set("culling_subscription_type", userDetails.culling_subscription_type);
      universalStorage.set("user_currency", userDetails.user_currency);
    }
  } catch (e) {
    logger.error("getUserDetails error", e);
  }
}

async function setAppConfig() {
  try {
    const appConfig = await getAppConfig();
    universalStorage.set("app_config", appConfig);
  } catch (e) {
    logger.error("Error in getting app config: ", e);
  }
}

const DEFAULT_AUTH_COOKIES_CONFIG_DEV = {
  httpOnly: true,
  domain: "localhost",
  sameSite: "none",
  path: "/",
  secure: true,
} as const;

const DEFAULT_AUTH_COOKIES_CONFIG_PROD = {
  httpOnly: true,
  domain: "imagen-ai.com",
  sameSite: "none",
  path: "/",
  secure: true,
} as const;

function getAuthCookieConfig(Astro: APIContext) {
  if (import.meta.env.MODE === "development") return DEFAULT_AUTH_COOKIES_CONFIG_DEV;

  // we use localhost for the cookie domain if we're testing locally
  const domain = Astro.url.hostname === "localhost" ? "localhost" : "imagen-ai.com";
  return { ...DEFAULT_AUTH_COOKIES_CONFIG_PROD, domain };
}
